/**
 * Método Page - Editorial Style
 * Complete methodology with all 5 phases
 */

import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { PageWrapper } from '../components/EditorialShared';

// Method phases - COMPLETE data
const methodPhases = [
  {
    number: "01",
    title: "Diagnóstico",
    subtitle: "Escucha activa y análisis profundo",
    description: "Comenzamos escuchándote. Analizamos tu marca, tu mercado, tu competencia y tus objetivos. Identificamos oportunidades y áreas de mejora.",
    deliverables: [
      "Entrevista estratégica con el cliente",
      "Análisis de marca y posicionamiento actual",
      "Estudio de competencia directa e indirecta",
      "Identificación de audiencia objetivo",
      "Resumen ejecutivo de hallazgos"
    ],
    output: "Brief estratégico completo",
    duration: "1-2 semanas"
  },
  {
    number: "02",
    title: "Estrategia",
    subtitle: "Plan de acción personalizado",
    description: "Definimos objetivos claros y medibles. Creamos un roadmap estratégico con acciones concretas y KPIs para cada etapa.",
    deliverables: [
      "Definición de objetivos SMART",
      "Estrategia de contenido y comunicación",
      "Calendario editorial mensual",
      "Plan de pauta digital (si aplica)",
      "Presupuesto y timeline detallado"
    ],
    output: "Plan estratégico a 3-6 meses",
    duration: "1 semana"
  },
  {
    number: "03",
    title: "Producción",
    subtitle: "Contenido de alto impacto",
    description: "Ejecutamos la producción de contenido visual y audiovisual. Fotografía profesional, video, motion graphics y diseño gráfico.",
    deliverables: [
      "Sesiones fotográficas profesionales",
      "Producción de video y reels",
      "Motion graphics y animaciones",
      "Diseño de piezas para redes",
      "Desarrollo de look & feel visual"
    ],
    output: "Banco de contenido + assets",
    duration: "2-4 semanas"
  },
  {
    number: "04",
    title: "Activación",
    subtitle: "Lanzamiento y ejecución",
    description: "Ponemos en marcha las campañas. Gestionamos tus redes sociales, lanzamos pauta digital y ejecutamos las acciones planificadas.",
    deliverables: [
      "Publicación de contenido optimizado",
      "Community management activo",
      "Lanzamiento de campañas de pauta",
      "Coordinación con equipo interno",
      "Comunicación constante y reportes"
    ],
    output: "Campañas activas + gestión continua",
    duration: "Continuo"
  },
  {
    number: "05",
    title: "Optimización",
    subtitle: "Mejora continua basada en datos",
    description: "Medimos, analizamos y optimizamos constantemente. Ajustamos estrategias basándonos en resultados reales y aprendizajes.",
    deliverables: [
      "Reportes mensuales de resultados",
      "Análisis de métricas clave (KPIs)",
      "A/B testing de contenido y pauta",
      "Recomendaciones de optimización",
      "Sesiones de revisión estratégica"
    ],
    output: "Reportes + plan de optimización",
    duration: "Mensual"
  }
];

// Phase tabs for navigation
const PhaseTabs = ({ activePhase, setActivePhase }) => {
  return (
    <div className="flex flex-wrap justify-center gap-2 mb-16">
      {methodPhases.map((phase, i) => (
        <button
          key={phase.number}
          onClick={() => setActivePhase(i)}
          className={`px-4 py-2 text-sm uppercase tracking-wider transition-all ${
            activePhase === i 
              ? 'text-white border-b-2 border-white' 
              : 'text-white/40 hover:text-white/70'
          }`}
        >
          {phase.title}
        </button>
      ))}
    </div>
  );
};

// Phase detail component
const PhaseDetail = ({ phase, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      className="grid md:grid-cols-12 gap-8 md:gap-16 py-16 md:py-24 border-t border-white/10"
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      {/* Number */}
      <div className="md:col-span-2">
        <span className="text-white/20 text-6xl md:text-8xl font-extralight">
          {phase.number}
        </span>
      </div>

      {/* Main content */}
      <div className="md:col-span-5">
        <span className="text-white/40 text-sm uppercase tracking-wider block mb-2">
          Fase {phase.number}
        </span>
        <h2 className="text-white text-4xl md:text-5xl font-extralight mb-3">
          {phase.title}
        </h2>
        <p className="text-white text-lg mb-6">
          {phase.subtitle}
        </p>
        <p className="text-white/60 text-lg font-light leading-relaxed">
          {phase.description}
        </p>
      </div>

      {/* Deliverables */}
      <div className="md:col-span-5">
        <h4 className="text-white/40 text-sm uppercase tracking-wider mb-4">
          Incluye
        </h4>
        <ul className="space-y-3 mb-8">
          {phase.deliverables.map((item, i) => (
            <motion.li
              key={i}
              className="text-white/70 text-base font-light flex items-start gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: 0.4 + i * 0.08 }}
            >
              <span className="w-1.5 h-1.5 bg-[#E66115] rounded-full mt-2 flex-shrink-0" />
              {item}
            </motion.li>
          ))}
        </ul>

        {/* Output and duration */}
        <div className="flex flex-col gap-4 pt-6 border-t border-white/10">
          <div>
            <span className="text-white/40 text-xs uppercase tracking-wider">Entregable</span>
            <p className="text-white text-base">{phase.output}</p>
          </div>
          <div>
            <span className="text-white/40 text-xs uppercase tracking-wider">Duración</span>
            <p className="text-white text-base">{phase.duration}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Progress indicator
const ProgressLine = () => {
  const { scrollYProgress } = useScroll();
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <div className="fixed left-8 top-1/2 -translate-y-1/2 h-[200px] w-px bg-white/10 hidden lg:block z-50">
      <motion.div
        className="absolute top-0 left-0 w-full bg-[#E66115] origin-top"
        style={{ scaleY, height: '100%' }}
      />
    </div>
  );
};

// Main page component
const EditorialMetodoPage = () => {
  const heroRef = useRef(null);
  const isHeroInView = useInView(heroRef, { once: true });
  const [activePhase, setActivePhase] = useState(0);

  return (
      <PageWrapper showLogoFooter={true}>
        {/* Hero */}
        <section className="min-h-[80vh] flex items-center pt-20" ref={heroRef}>
          <div className="max-w-5xl mx-auto px-6">
            <motion.span
              className="text-white/40 font-medium text-sm tracking-widest uppercase mb-6 block"
              initial={{ opacity: 0, y: 20 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
            >
              Nuestro Proceso
            </motion.span>
            
            <motion.h1
              className="text-white text-5xl md:text-7xl lg:text-8xl font-extralight leading-[0.95] mb-8"
              initial={{ opacity: 0, y: 60 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1, delay: 0.5, ease: [0.25, 0.1, 0, 1] }}
            >
              El Método
              <br />
              <span className="italic">Paliacate</span>
            </motion.h1>

            <motion.p
              className="text-white/50 text-xl md:text-2xl font-light max-w-2xl leading-relaxed"
              initial={{ opacity: 0, y: 40 }}
              animate={isHeroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.8 }}
            >
              Un sistema probado de 5 fases para transformar tu marca 
              en una máquina de crecimiento. Estrategia, producción y resultados medibles.
            </motion.p>
          </div>
        </section>

        {/* Phase tabs */}
        <section className="max-w-5xl mx-auto px-6">
          <PhaseTabs activePhase={activePhase} setActivePhase={setActivePhase} />
        </section>

        {/* Method phases */}
        <section className="max-w-6xl mx-auto px-6">
          {methodPhases.map((phase, index) => (
            <PhaseDetail key={phase.number} phase={phase} index={index} />
          ))}
        </section>

        {/* Differentiator section */}
        <section className="py-24 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.h2
              className="text-white text-3xl md:text-5xl font-extralight mb-8"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              No somos una agencia más
            </motion.h2>

            <motion.p
              className="text-white/50 text-lg md:text-xl font-light mb-12 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
            >
              Somos tu socio estratégico. No vendemos seguidores ni likes vacíos. 
              Construimos sistemas de comunicación que generan resultados reales 
              y medibles para tu negocio.
            </motion.p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              {[
                { value: "100%", label: "Transparencia en resultados" },
                { value: "0", label: "Métricas de vanidad" },
                { value: "∞", label: "Compromiso con tu éxito" },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="text-white text-4xl md:text-5xl font-extralight mb-2">
                    {stat.value}
                  </div>
                  <div className="text-white/40 text-sm">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <motion.h2
              className="text-white text-3xl md:text-5xl font-extralight mb-6"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Comienza tu transformación
            </motion.h2>

            <motion.p
              className="text-white/50 text-lg font-light mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              viewport={{ once: true }}
            >
              El primer paso es un diagnóstico estratégico donde analizaremos 
              tu situación actual y diseñaremos un plan personalizado.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              viewport={{ once: true }}
            >
              <a
                href="https://wa.me/527774385617"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 bg-white text-black font-medium hover:bg-white/90 transition-colors"
                data-cursor="agendar"
              >
                Agendar Diagnóstico Gratuito
              </a>
              <a
                href="https://wa.me/527774385617"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-4 border border-white/20 text-white hover:bg-white/5 transition-colors"
                data-cursor="whatsapp"
              >
                Contactar por WhatsApp
              </a>
            </motion.div>
          </div>
        </section>
      </PageWrapper>
  );
};

export default EditorialMetodoPage;
